import { Namespace, useT } from "@racoon/i18n";
import {
  humanTruckType,
  isShiftRunning,
  isShiftTerminal,
  resolveShiftReleasedAt,
} from "../helpers";
import { isTruckArchived } from "~/shared/lib/truck-type";
import {
  printSingleWaybill,
  type ShiftData as PrintShiftData,
} from "../helpers/print-waybill.helper";
import {
  useGetOrganizationSettingsQuery,
  useGetOrganizationByIdQuery,
} from "~/shared/api/organization/organization.grpc";
import { useListTrucksQuery } from "~/shared/api/truck/truck.grpc";
import { useGetTruckLatestReadingsQuery } from "~/shared/api/analytics/analytics.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { Button, CountBadge, DatePicker } from "@racoon/ui";
import { useListUsersQuery } from "~/shared/api/user/user.grpc";
import { useListTruckDriverOnShiftsQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { useState, useMemo, useCallback, useRef } from "react";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp, toTimestamp } from "@racoon/lib";
import { TruckCard } from "./autopark.truck-card.admin";
import { useSearchParams } from "react-router-dom";
import { useShiftApproval, type ShiftId } from "../hooks/useShiftApproval";

export const TruckTab = () => {
  const t = useT();
  const { activeCompanyId, activeCompany: organization } = useAuthSession();
  const company_id = activeCompanyId ?? undefined;
  const selectedCompanyId = company_id;

  const selectedCompanyIdRef = useRef(selectedCompanyId);
  selectedCompanyIdRef.current = selectedCompanyId;
  const [sp, setSp] = useSearchParams();
  const dateParam = sp.get("date");

  const selectedDate = useMemo(() => {
    if (dateParam) {
      const [y, m, d] = dateParam.split("-").map(Number);
      if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
        return new Date(y, m - 1, d);
      }
    }
    return new Date();
  }, [dateParam]);

  const setSelectedDate = useCallback(
    (date: Date) => {
      const next = new URLSearchParams(sp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      next.set("date", `${year}-${month}-${day}`);
      setSp(next, { replace: true });
    },
    [sp, setSp],
  );
  const [selected_type, set_selected_type] = useState<ConnectProto.TruckType | "ALL">("ALL");
  const [show_archive, set_show_archive] = useState(false);

  const startOfDay = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return toTimestamp(d);
  }, [selectedDate]);

  const endOfDay = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(23, 59, 59, 999);
    return toTimestamp(d);
  }, [selectedDate]);

  const { data: trucksData } = useListTrucksQuery(
    {
      ownerOrganizationIds: company_id ? [BigInt(company_id)] : [],
    },
    { skip: !company_id },
  );

  // One org-wide fetch per grid; cards join by truck id.
  const { data: readingsData } = useGetTruckLatestReadingsQuery(
    { ownerOrganizationId: company_id ? BigInt(company_id) : 0n },
    { skip: !company_id },
  );

  const readingsByTruckId = useMemo(() => {
    const map = new Map<string, ConnectProto.TruckLatestReadingsRow>();
    for (const row of readingsData?.rows || []) {
      if (row.truckId != null) map.set(String(row.truckId), row);
    }
    return map;
  }, [readingsData?.rows]);

  const { data: shiftData, refetch: refetchShifts } = useListTruckDriverOnShiftsQuery(
    {
      ownerOrganizationId: company_id ? BigInt(company_id) : 0n,
      datetimeFilter: {
        from: startOfDay,
        to: endOfDay,
      },
    },
    { skip: !company_id },
  );

  const trucks = useMemo(() => {
    const list = trucksData?.trucks || [];
    const shiftsMap = new Map<bigint, ConnectProto.DriverOnShift[]>();

    if (shiftData?.truckDriverOnShifts) {
      for (const ts of shiftData.truckDriverOnShifts) {
        if (ts.truck?.id && ts.driverOnShifts) {
          shiftsMap.set(ts.truck.id, ts.driverOnShifts);
        }
      }
    }

    return list.map((truck) => {
      const truckShifts = shiftsMap.get(truck.id) || [];
      const currentShift = truckShifts.find((s) => !isShiftTerminal(s.status));

      return {
        truck,
        currentShift,
        shifts: truckShifts,
      } as unknown as ConnectProto.CurrentTruckStatus & { shifts?: ConnectProto.DriverOnShift[] };
    });
  }, [trucksData?.trucks, shiftData?.truckDriverOnShifts]);

  const trucksRef = useRef(trucks);
  trucksRef.current = trucks;

  // Get active drivers
  const { data: driversResponse } = useListUsersQuery(
    {
      ownerOrganizationIds: company_id ? [BigInt(company_id)] : [],
      roles: [ConnectProto.UserRole.DRIVER],
      statuses: [ConnectProto.UserStatus.ACTIVE],
    },
    { skip: !company_id },
  );
  const drivers = driversResponse?.users || [];
  // Get current organization settings
  const { data: organization_settings } = useGetOrganizationSettingsQuery(
    {},
    {
      skip: !company_id,
    },
  );

  // Get current organization data
  const { data: organization_data } = useGetOrganizationByIdQuery(
    { organizationId: BigInt(company_id!) },
    { skip: !company_id },
  );

  const getShifts = useCallback(() => {
    return (trucksRef.current || []).flatMap((t) => {
      const truckShifts = t.shifts || [];
      return truckShifts.map((s) => ({
        id: s.id,
        status: s.status,
        truckId: s.truck?.id || t.truck?.id,
        driverId: s.driver?.id,
        driverName: [s.driver?.firstName, s.driver?.lastName].filter(Boolean).join(" "),
        plateNumber: s.truck?.plateNumber || t.truck?.plateNumber,
        startedAt: s.startedAt,
        twStartAt: s.twStartAt,
        createdAt: s.createdAt,
      }));
    });
  }, []);

  const {
    approveShift: handle_driver_approve,
    completeShift,
    cancelShift,
    isCompletingShift: is_completing_shift,
    isShiftBusy: is_shift_busy,
  } = useShiftApproval({
    selectedCompanyId,
    getShifts,
    refetch: refetchShifts,
  });

  const handle_shift_complete = useCallback(
    (shift_id: ShiftId) => {
      const shifts = getShifts();
      const shift = shifts.find((s) => String(s.id) === String(shift_id));
      return completeShift({ id: shift_id, driverName: shift?.driverName });
    },
    [completeShift, getShifts],
  );

  // Function to print shift permit using shared helper
  const print_shift_permit = useCallback(
    async (shift: ConnectProto.DriverOnShift, truck: ConnectProto.Truck) => {
      const driver = shift.driver;
      if (!driver) return;

      const releasedAt = resolveShiftReleasedAt(shift);
      const completedAt = fromTimestamp(shift.completedAt)?.toISOString();

      const print_data: PrintShiftData = {
        id: String(shift.id),
        status: shift.status,
        plateNumber: truck.plateNumber || "",
        driverName:
          `${driver.firstName || ""} ${driver.lastName || ""} №${driver.driverLicenseNumber || ""}`.trim(),
        // truck_type is the current source; truck.type is the deprecated fallback
        type: String(truck.truckType?.type || truck.type || ""),
        model: truck.model || "",
        trailer: truck.techPassport || "",
        releasedAt,
        twStartAt: fromTimestamp(shift.twStartAt)?.toISOString(),
        mileageBefore: Number(shift.odometerReading ?? 0),
        acceptedAt: completedAt,
        completed_at: completedAt,
      };

      await printSingleWaybill(print_data, {
        organization: organization_data || organization || undefined,
        organizationSettings: organization_settings,
        t,
      });
    },
    [organization_data, organization, organization_settings, t],
  );

  const sorted_trucks = useMemo(() => {
    if (!trucks) return [];
    return [...trucks].sort((a: any, b: any) => {
      const getShiftScore = (x: any) => {
        const shifts = x.shifts || [];
        if (shifts.length === 0) return 0;

        const hasActive = shifts.some((shift: ConnectProto.DriverOnShift) =>
          isShiftRunning(shift.status),
        );
        if (hasActive) return 3;

        const hasPlanned = shifts.some(
          (shift: ConnectProto.DriverOnShift) =>
            shift.status === ConnectProto.DriverOnShiftStatus.NOT_APPROVED,
        );
        if (hasPlanned) return 2;

        // Cancelled counts here too: the truck had a shift today and it is over.
        const hasCompleted = shifts.some((shift: ConnectProto.DriverOnShift) =>
          isShiftTerminal(shift.status),
        );
        if (hasCompleted) return 1;

        return 0;
      };

      const scoreA = getShiftScore(a);
      const scoreB = getShiftScore(b);

      if (scoreA !== scoreB) {
        return scoreB - scoreA;
      }
      const a_status = a.truck?.status ?? 999;
      const b_status = b.truck?.status ?? 999;
      if (a_status !== b_status) return b_status - a_status;
      return (a.truck?.plateNumber || "").localeCompare(b.truck?.plateNumber || "");
    });
  }, [trucks]);

  const archived_count = useMemo(
    () => (trucks || []).filter((t) => isTruckArchived(t.truck)).length,
    [trucks],
  );

  // Archive tab first, truck type second — the type pills count within the tab.
  const archive_filtered = useMemo(
    () => sorted_trucks.filter((t) => isTruckArchived(t.truck) === show_archive),
    [sorted_trucks, show_archive],
  );

  const type_counts = useMemo(() => {
    const counts = new Map<ConnectProto.TruckType, number>();
    for (const t of archive_filtered) {
      const type = t.truck?.type;
      if (type != null) counts.set(type, (counts.get(type) || 0) + 1);
    }
    return counts;
  }, [archive_filtered]);

  const filtered_trucks = useMemo(() => {
    if (selected_type === "ALL") return archive_filtered;
    // Compare truck type as numbers since TruckType enum values are numeric
    return archive_filtered.filter((t) => t.truck?.type === Number(selected_type));
  }, [archive_filtered, selected_type]);

  const truck_types = [
    ConnectProto.TruckType.MSW,
    ConnectProto.TruckType.CONSTRUCTION,
    ConnectProto.TruckType.DOUBLE_CONTAINER,
    ConnectProto.TruckType.TIPPER,
    ConnectProto.TruckType.LIQUID_WASTE,
    ConnectProto.TruckType.LOADER_CRANE,
  ];

  return (
    // mt-2 on top of TabsContent's own mt-2 makes 16px, the gap FilterBlock puts
    // between its rows — so this toolbar lines up with the registry's controls row.
    <div className="space-y-4 mt-2">
      <div className="flex justify-between items-center flex-wrap gap-2">
        {/* Date picker */}
        <div className="flex items-center gap-2">
          <DatePicker
            id="truck-tab-date-picker"
            showDayNav
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            navWrapperClassName="flex items-center gap-1"
            navButtonClassName="!px-2"
            className="flex w-[auto] bg-white items-center rounded-lg gap-1 px-3 py-1 border border-input !h-8"
          />
          {/* Active / archive tabs */}
          <Button
            data-testid="truck-tab-filter-active"
            variant={!show_archive ? "default" : "opacityBlueWithoutOutline"}
            onClick={() => set_show_archive(false)}
            className="!rounded-full !h-6 !px-2"
            size="sm"
          >
            {t(Namespace.Autopark, "filters.activeTrucks")}
            <CountBadge
              count={(trucks?.length || 0) - archived_count}
              active={!show_archive}
              className="ml-1.5"
            />
          </Button>
          <Button
            data-testid="truck-tab-filter-archived"
            variant={show_archive ? "default" : "opacityBlueWithoutOutline"}
            onClick={() => set_show_archive(true)}
            className="!rounded-full !h-6 !px-2"
            size="sm"
          >
            {t(Namespace.Autopark, "filters.archivedTrucks")}
            <CountBadge count={archived_count} active={show_archive} className="ml-1.5" />
          </Button>
        </div>
        {/* Filters by types */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          <Button
            data-testid="truck-tab-filter-type-all"
            variant={selected_type === "ALL" ? "default" : "opacityBlueWithoutOutline"}
            onClick={() => set_selected_type("ALL")}
            className="!rounded-full !h-6 !px-2"
            size="sm"
          >
            {t(Namespace.Common, "allTrucks")}
            <CountBadge
              count={archive_filtered.length}
              active={selected_type === "ALL"}
              className="ml-1.5"
            />
          </Button>
          {truck_types.map((type) => (
            <Button
              key={type}
              data-testid={`truck-tab-filter-type-${type}`}
              variant={selected_type === type ? "default" : "opacityBlueWithoutOutline"}
              onClick={() => set_selected_type(type)}
              className="!rounded-full !h-6 !px-2"
              size="sm"
            >
              {humanTruckType(type, t)}
              <CountBadge
                count={type_counts.get(type) || 0}
                active={selected_type === type}
                className="ml-1.5"
              />
            </Button>
          ))}
        </div>
      </div>

      {/* Trucks grid */}
      {/* Cards stay square and identically sized; auto-fill decides how many fit per row. */}
      <div className="grid max-h-[calc(100dvh-8rem)] overflow-y-scroll grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-3">
        {filtered_trucks.length === 0 ? (
          <div className="text-center text-muted-foreground col-span-full py-4">
            {t(Namespace.Common, "noResults")}
          </div>
        ) : (
          filtered_trucks.map((truckStatus) => (
            <TruckCard
              key={truckStatus.truck?.id}
              truckStatus={truckStatus}
              readings={
                truckStatus.truck?.id != null
                  ? readingsByTruckId.get(String(truckStatus.truck.id))
                  : undefined
              }
              available_drivers={drivers}
              is_completing_shift={is_completing_shift}
              is_shift_busy={is_shift_busy}
              on_approved_shift={handle_driver_approve}
              on_print_shift={print_shift_permit}
              on_complete_shift={handle_shift_complete}
              on_cancel_shift={cancelShift}
              defaultDate={selectedDate}
            />
          ))
        )}
      </div>
    </div>
  );
};
